import { Link, useLocation } from 'react-router-dom';
import BottomNav from './BottomNav.js';
import { useAuthStore } from '../stores/auth-store.js';
import { usePushNotifications } from '../hooks/usePushNotifications.js';
import { useState, useEffect } from 'react';

// SVG icon components — lightweight, no dependencies





// All nav items with dealer-only flag




const ALL_MENU_ITEMS: { path: string; label: string; dealerOnly: boolean; individualOnly?: boolean }[] = [
  { path: '/connect', label: 'Connect Accounts', dealerOnly: false },
  { path: '/customers', label: 'Customers', dealerOnly: true },
  { path: '/website', label: 'My Website', dealerOnly: true },
  { path: '/settings', label: 'Settings', dealerOnly: false },
  { path: '/billing', label: 'Billing', dealerOnly: true },
  { path: '/settings#upgrade', label: 'Upgrade to Dealer', dealerOnly: false, individualOnly: true },
];

export default function Layout({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  const dealership = useAuthStore((s) => s.dealership);
  const isDealer = useAuthStore((s) => s.isDealer);
  const logout = useAuthStore((s) => s.logout);
  const accounts = useAuthStore((s) => s.accounts);
  const switchAccount = useAuthStore((s) => s.switchAccount);
  const [menuOpen, setMenuOpen] = useState(false);
  const { permission, requestPermission } = usePushNotifications();
  const [showPushBanner, setShowPushBanner] = useState(false);

  const isAdmin = user?.role === 'admin' || user?.role === 'owner';
  const dealer = isDealer();

  // Filter nav/menu based on account type
  const menuItems = ALL_MENU_ITEMS.filter((item) => (!item.dealerOnly || dealer) && (!item.individualOnly || !dealer));

  // Show push notification prompt once after a brief delay
  useEffect(() => {
    if (permission === 'default') {
      const prompted = sessionStorage.getItem('cdos-push-prompted');
      if (!prompted) {
        const timer = setTimeout(() => setShowPushBanner(true), 5000);
        return () => clearTimeout(timer);
      }
    }
  }, [permission]);

  const handlePushAccept = async () => {
    await requestPermission();
    sessionStorage.setItem('cdos-push-prompted', '1');
    setShowPushBanner(false);
  };

  const handlePushDismiss = () => {
    sessionStorage.setItem('cdos-push-prompted', '1');
    setShowPushBanner(false);
  };

  return (
    <div className="min-h-screen bg-surface-900 text-white">
      {/* Push Notification Banner */}
      {showPushBanner && (
        <div className="fixed top-0 left-0 right-0 z-[60] bg-brand-700 text-white px-4 py-3 flex items-center justify-between animate-fadeIn">
          <p className="text-sm font-medium">Get notified about new leads and updates</p>
          <div className="flex gap-2 ml-3 shrink-0">
            <button onClick={handlePushAccept} className="bg-white text-brand-700 text-xs font-bold px-3 py-1 rounded-lg">
              Enable
            </button>
            <button onClick={handlePushDismiss} className="text-white/70 text-xs px-2 py-1">
              Later
            </button>
          </div>
        </div>
      )}

      {/* Top Bar */}
      <header className="sticky top-0 z-50 bg-surface-900/80 backdrop-blur-xl border-b border-surface-800 pt-safe">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
        <h1 className="text-lg font-bold tracking-tight">{dealership?.name ?? 'AutoCarPost'}</h1>
        <div className="relative">
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="text-surface-500 text-sm hover:text-white transition-colors px-2 py-1 flex items-center gap-1"
          >
            <span className="w-7 h-7 bg-brand-600 rounded-full flex items-center justify-center text-xs font-bold text-white">
              {(user?.name ?? 'U')[0].toUpperCase()}
            </span>
            <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor" className={`transition-transform ${menuOpen ? 'rotate-180' : ''}`}>
              <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" />
            </svg>
          </button>
          {menuOpen && (
            <div className="absolute right-0 top-full mt-1 bg-surface-800 border border-surface-700 rounded-lg shadow-xl min-w-[200px] py-1 z-50 animate-fadeIn">
              <div className="px-4 py-2 border-b border-surface-700">
                <p className="text-sm font-medium">{user?.name}</p>
                <p className="text-xs text-surface-500">{user?.role}{!dealer ? ' · Individual' : ''}</p>
              </div>
              {accounts.length > 1 && (
                <div className="border-b border-surface-700 py-1">
                  <p className="px-4 py-1 text-[10px] uppercase tracking-wider text-surface-600 font-medium">Switch Account</p>
                  {accounts.map((acct, i) => (
                    acct.user.id !== user?.id && (
                      <button
                        key={acct.user.id}
                        onClick={() => { switchAccount(i); setMenuOpen(false); }}
                        className="flex items-center gap-2 w-full px-4 py-2 text-sm text-surface-300 hover:bg-surface-700 transition-colors"
                      >
                        <span className="w-6 h-6 bg-surface-600 rounded-full flex items-center justify-center text-[10px] font-bold text-white shrink-0">
                          {acct.dealership.name[0].toUpperCase()}
                        </span>
                        <span className="truncate">{acct.dealership.name}</span>
                      </button>
                    )
                  ))}
                </div>
              )}
              {menuItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMenuOpen(false)}
                  className={`block px-4 py-2 text-sm hover:bg-surface-700 transition-colors ${
                    location.pathname === item.path ? 'text-brand-400' : 'text-surface-300'
                  }`}
                >
                  {item.label}
                </Link>
              ))}
              {isAdmin && (
                <Link
                  to="/admin"
                  onClick={() => setMenuOpen(false)}
                  className={`block px-4 py-2 text-sm hover:bg-surface-700 transition-colors ${
                    location.pathname === '/admin' ? 'text-brand-400' : 'text-red-400'
                  }`}
                >
                  Admin Panel
                </Link>
              )}
              <hr className="border-surface-700 my-1" />
              <button
                onClick={() => { logout(); setMenuOpen(false); }}
                className="block w-full text-left px-4 py-2 text-sm text-surface-500 hover:text-white hover:bg-surface-700 transition-colors"
              >
                Logout
              </button>
            </div>
          )}
        </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-5xl mx-auto pb-24" onClick={() => menuOpen && setMenuOpen(false)}>
        {children}
      </main>

        <BottomNav />
    </div>
  );
}
